import { commaSeparatedNumber } from "../../../helpers/utils";
import { ReusableTable } from "./ReusableTable";
import { TrafficByDevice } from "./TrafficByDevice";

export function ReportingTab({ userId, data }: { userId: string; data: any }) {
  // Transform API data for tables
  const funnelData =
    data?.funnelSummary?.map((item: any, index: number) => ({
      id: index + 1,
      event: item.event,
      hash: commaSeparatedNumber(item.count || 0, false, false),
      cvr: commaSeparatedNumber(item.cvr || 0, true, false),
      value: commaSeparatedNumber(item.value || 0, false, true),
    })) || [];

  const blankRow = {
    id: "-",
    event: "-",
    hash: "-",
    percentage: "-",
    value: "-",
  };

  const abandonedCartData = [
    blankRow,
    ...(data?.abandonedCartSummary?.map((item: any, index: number) => ({
      id: index + 1,
      event: item.event?.replace(/([A-Z])/g, " $1").trim(),
      hash: commaSeparatedNumber(item.count || 0, false, false),
      percentage: commaSeparatedNumber(item.percent || 0, true, false),
      value: commaSeparatedNumber(item.value || 0, false, true),
    })) || []),
  ];

  const campaignData =
    data?.campaignSummary?.map((item: any, index: number) => ({
      id: index + 1,
      campaign: item.campaignName,
      impressions: commaSeparatedNumber(item.impressions || 0, false, false),
      clicks: commaSeparatedNumber(item.clicks || 0, false, false),
      ctr: commaSeparatedNumber(item.ctr || 0, true, false),
      addToCart: commaSeparatedNumber(item.atc || 0, false, false),
      beginCheckout: commaSeparatedNumber(item.bco || 0, false, false),
      purchases: commaSeparatedNumber(item.pur || 0, false, false),
      purchaseValue: commaSeparatedNumber(item.purchaseValue || 0, false, true),
      cvr: commaSeparatedNumber(item.cvr || 0, true, false),
    })) || [];

  const deviceData =
    data?.deviceSummary?.map((item: any, index: number) => ({
      id: index + 1,
      device: item.device,
      visits: commaSeparatedNumber(item.clicks || 0, false, false),
      percentage: commaSeparatedNumber(item.ctr || 0, true, false),
      conversions: commaSeparatedNumber(item.conversionValue || 0, false, true),
    })) || [];

  const feederMarketsData =
    data?.feederMarkets?.map((item: any, index: number) => ({
      id: index + 1,
      market: item.city,
      visitors: commaSeparatedNumber(item.impressions || 0, false, false),
      clicks: commaSeparatedNumber(item.clicks || 0, false, false),
      conversions: commaSeparatedNumber(
        item.conversions_value || 0,
        false,
        true
      ),
    })) || [];

  // Table column definitions
  const funnelColumns = [
    { key: "event", label: "Event" },
    { key: "hash", label: "#", align: "right" as const },
    { key: "cvr", label: "CVR", align: "right" as const },
    { key: "value", label: "Bookings $", align: "right" as const },
  ];

  const abandonedCartColumns = [
    { key: "event", label: "Event" },
    { key: "hash", label: "#", align: "right" as const },
    { key: "percentage", label: "%", align: "right" as const },
    { key: "value", label: "Bookings $", align: "right" as const },
  ];

  const campaignColumns = [
    { key: "campaign", label: "Campaign" },
    { key: "impressions", label: "Impressions", align: "right" as const },
    { key: "clicks", label: "Clicks", align: "right" as const },
    { key: "ctr", label: "CTR", align: "right" as const },
    { key: "addToCart", label: "Add-To-Cart", align: "right" as const },
    { key: "beginCheckout", label: "Begin-Checkout", align: "right" as const },
    { key: "purchases", label: "Purchases", align: "right" as const },
    { key: "purchaseValue", label: "Bookings $", align: "right" as const },
    { key: "cvr", label: "CVR", align: "right" as const },
  ];

  const deviceColumns = [
    { key: "device", label: "Device" },
    { key: "visits", label: "Clicks", align: "right" as const },
    { key: "percentage", label: "CTR", align: "right" as const },
    { key: "conversions", label: "Bookings $", align: "right" as const },
  ];

  const feederMarketsColumns = [
    { key: "market", label: "Market" },
    { key: "clicks", label: "Clicks", align: "right" as const },
    { key: "visitors", label: "Impressions", align: "right" as const },
    { key: "conversions", label: "Bookings $", align: "right" as const },
  ];

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="lg:col-span-2 xl:col-span-2">
          <TrafficByDevice data={data} />
        </div>
        <div>
          <ReusableTable
            columns={funnelColumns}
            data={funnelData}
            label="Funnel Summary"
            infoTooltip="This funnel table tracks your user journey from ad impression all the way through purchase.<br><strong>#</strong> = Count of events at each stage<br><strong>CVR</strong> = Conversion rate to the next step (e.g. Clicks → Add to Cart)<br><strong>Bookings $</strong> = Booking value generated at that stage"
            id="funnel-summary"
            rowHeight={57.5}
          />
        </div>
        <div>
          <ReusableTable
            columns={abandonedCartColumns}
            data={abandonedCartData}
            label="Abandoned Cart Summary"
            infoTooltip="This table highlights where shoppers drop off in the checkout flow and how much potential revenue is lost vs. recovered through remarketing.<br><strong>Abandoned Carts</strong>: # of guests that began to add rooms to their cart, but did not 'begin to check-out'<br><strong>Abandoned Check-Outs</strong>: Checkout started but not completed<br><strong>Total Lost Bookings</strong>: Sum of the two above<br><strong>Bookings Recovered</strong>: Bookings made from users that previously abandoned their booking journey.<br><strong>Note</strong>: Sometimes bookings recovered could be from different month/period."
            id="abandoned-cart-summary"
            rowHeight={57.5}
          />
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="lg:col-span-2 xl:col-span-2">
          <ReusableTable
            columns={campaignColumns}
            data={campaignData}
            label="Campaign Summary"
            infoTooltip="This table compares performance metrics across each campaign strategy:<br>- <strong>Impressions / Clicks / CTR</strong> measure reach and engagement<br>- <strong>Add-To-Cart / Begin-Checkout / Purchases</strong> show conversion volume for each action.<br>- <strong>Bookings $</strong> is total revenue generated<br>- <strong>CVR</strong> is 'Conversion Rate' - the percent of clicks that result in a booking"
            id="campaign-summary"
            rowHeight={campaignData.length <= 4 ? 81 : 60}
            stickyLastRow={true}
          />
        </div>
        <div>
          <ReusableTable
            columns={deviceColumns}
            data={deviceData}
            label="Device Summary"
            infoTooltip="This table breaks down ad performance by device type.<br> It's important to optimize your booking journey / experience on each device."
            id="device-summary"
            rowHeight={deviceData.length <= 4 ? 81 : 60}
            stickyLastRow={true}
          />
        </div>
        <div>
          <ReusableTable
            columns={feederMarketsColumns}
            data={feederMarketsData}
            label="Feeder Markets"
            infoTooltip="This table shows where your guests are coming from. <br><strong>Note</strong>: this is based most often times on the user's current location, not necessarily their permanent location."
            id="feeder-markets"
            rowHeight={60}
          />
        </div>
      </div>
    </div>
  );
}
